import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  CreditCard,
  DollarSign,
  Home,
  LifeBuoy,
  LogOut,
  Mail,
  MessageSquare,
  Menu,
  Settings,
  ShieldCheck,
  UserCircle,
  Users,
  X,
} from 'lucide-react';
import { GROUP, MONEY, PAGE, PORTAL_NAME, SupportDownloadRail, formatCreditsWithUnit } from '@kioskgaming/ui';
import { useAuth } from '../../hooks/useAuth';
import { useWaitingDeviceCount } from '../../hooks/useWaitingDeviceCount';
import { useContactAdminUnread } from '../../hooks/useContactAdminUnread';
import { useAdminMessagesUnread } from '../../hooks/useAdminMessagesUnread';
import { CONTACT_ADMIN_LABEL, CONTACT_ADMIN_PATH } from '../contactAdmin/contactAdmin';
import { ADMIN_MESSAGES_LABEL, ADMIN_MESSAGES_PATH } from '../adminMessages/adminMessages';
import { SHOP_AUTH_TOKEN_CHANGED_EVENT, SHOP_CREDIT_BALANCE_REFRESH_EVENT } from '../../constants';
import { fetchDashboard } from '../../services/api';
import { isShopNavPathActive } from '../../utils/shopNavActive';
import {
  isLowCreditBalance,
  parseLowBalanceWarningSettings,
  type LowBalanceWarningSettings,
} from '../../utils/lowBalanceWarning';
import ShopNotificationBell from '../ShopNotificationBell';
import ShopInAppPopupGate from '../ShopInAppPopupGate';
import ShopDebtFreezeBanner from '../ShopDebtFreezeBanner';
import VerifyNowTrigger from '../VerifyNowTrigger';
import {
  APPROVALS_NAV_ITEMS,
  CASHIER_DEVICES_PATH,
  CASHIER_NAV_ITEMS,
  MESSAGES_NAV_ITEMS,
  MOBILE_NAV,
  NETWORK_NAV_ITEMS,
  POPUP_NAV_PATHS,
  SETTINGS_NAV_ITEMS,
  TRANSACTIONS_NAV_ITEMS,
  type NavItem,
} from '../../config/nav';
import vaultPlayLogo from '../../assets/brand/vault-play-logo.png';

/** Small amber count, e.g. devices waiting for approval. Renders nothing for 0. */
function NavCountBadge({ count, className = '' }: { count?: number; className?: string }) {
  if (!count || count <= 0) return null;
  const text = count > 99 ? '99+' : String(count);
  const label = `${count} ${count === 1 ? 'device' : 'devices'} waiting for approval`;
  return (
    <span
      className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold leading-5 text-white ${className}`}
      title={label}
      aria-label={label}
      data-testid="nav-waiting-badge"
    >
      {text}
    </span>
  );
}

function SidebarBalanceCard({
  balance,
  loading,
  lowBalanceWarning,
}: {
  balance: number | null;
  loading: boolean;
  lowBalanceWarning: LowBalanceWarningSettings;
}) {
  const low = isLowCreditBalance(balance, lowBalanceWarning);

  return (
    <div
      className={`rounded-lg border px-3 py-2.5 shadow-sm ${
        low && !loading
          ? 'border-amber-200 bg-amber-50'
          : 'border-stone-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div
            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
              low && !loading ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'
            }`}
          >
            <CreditCard className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-wide text-stone-500">{MONEY.creditBalance}</p>
            <p className="mt-0.5 text-[15px] font-bold leading-tight text-stone-900">
              {loading ? '…' : formatCreditsWithUnit(balance)}
            </p>
          </div>
        </div>
        {low && !loading ? (
          <span className="shrink-0 text-base text-amber-600" title="Low balance" aria-hidden>
            ⚠️
          </span>
        ) : null}
      </div>
    </div>
  );
}

function SidebarUsdBalanceCard({
  balance,
  loading,
}: {
  balance: number | null;
  loading: boolean;
}) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
            <DollarSign className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-wide text-stone-500">{MONEY.usdWalletBalance}</p>
            <p className="mt-0.5 text-[15px] font-bold leading-tight text-stone-900 tabular-nums">
              {loading ? '…' : `$${Number(balance ?? 0).toFixed(2)}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SidebarNavItem({
  to,
  end,
  icon: Icon,
  label,
  nested = false,
  match,
  badge,
  onClick,
}: {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
  nested?: boolean;
  match?: string[];
  badge?: number;
  onClick?: () => void;
}) {
  const location = useLocation();
  const active = isShopNavPathActive(location.pathname, { to, end, match });

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg border-l-[3px] font-medium transition-[background-color,border-color,color] duration-150 ease-in-out ${
        nested ? 'py-2 pl-[calc(0.5rem-3px)] pr-2 text-[13px]' : 'py-2.5 pl-[calc(0.75rem-3px)] pr-3 text-sm'
      } ${
        active
          ? `-ml-[3px] border-l-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.12)] ${nested ? 'pl-[calc(0.5rem+3px)]' : 'pl-[calc(0.75rem+3px)]'} text-[hsl(var(--primary))]`
          : 'border-l-transparent text-stone-600 hover:bg-stone-200/50 hover:text-stone-900'
      }`}
    >
      <Icon
        className={`shrink-0 ${nested ? 'h-4 w-4' : 'h-[18px] w-[18px]'} ${active ? 'text-[hsl(var(--primary))]' : 'opacity-80'}`}
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <NavCountBadge count={badge} />
    </NavLink>
  );
}

function SidebarNavGroup({
  label,
  icon: Icon,
  items,
  headerTo,
  badge,
  onItemClick,
}: {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  /** When set, the group header navigates here (Settings). Otherwise it only expands. */
  headerTo?: string;
  /** Count on the group header (e.g. devices waiting inside the group). */
  badge?: number;
  onItemClick?: () => void;
}) {
  const location = useLocation();
  const childActive = items.some((item) => isShopNavPathActive(location.pathname, item));
  const headerActive = headerTo
    ? location.pathname === headerTo || location.pathname.startsWith(`${headerTo}/`)
    : false;
  const sectionActive = childActive || headerActive;
  const [expanded, setExpanded] = useState(sectionActive);

  useEffect(() => {
    if (sectionActive) setExpanded(true);
  }, [sectionActive]);

  const toggle = () => setExpanded((open) => !open);
  const chevron = expanded ? (
    <ChevronDown className="h-4 w-4 shrink-0 text-stone-500" aria-hidden />
  ) : (
    <ChevronRight className="h-4 w-4 shrink-0 text-stone-500" aria-hidden />
  );
  const headerStateClass = sectionActive
    ? 'bg-stone-100 text-stone-900'
    : 'text-stone-600 hover:bg-stone-200/50 hover:text-stone-900';

  return (
    <div className="space-y-0.5">
      {headerTo ? (
        <div className={`flex items-stretch rounded-lg ${sectionActive ? 'bg-stone-100' : ''}`}>
          <NavLink
            to={headerTo}
            onClick={onItemClick}
            className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg py-2.5 pl-3 pr-1 text-left text-sm font-medium transition-colors ${
              headerActive ? 'text-stone-900' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            <Icon className="h-[18px] w-[18px] shrink-0 opacity-80" />
            <span className="flex-1 truncate">{label}</span>
            <NavCountBadge count={badge} />
          </NavLink>
          <button
            type="button"
            onClick={toggle}
            className="flex items-center rounded-lg px-2 text-stone-500 hover:bg-stone-200/50"
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          >
            {chevron}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggle}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${headerStateClass}`}
          aria-expanded={expanded}
        >
          <Icon className="h-[18px] w-[18px] shrink-0 opacity-80" />
          <span className="flex-1">{label}</span>
          <NavCountBadge count={badge} />
          {chevron}
        </button>
      )}
      {expanded ? (
        <div className="ml-3 space-y-0.5 border-l border-stone-200 pl-2">
          {items.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              end={item.end}
              icon={item.icon}
              label={item.label}
              match={item.match}
              badge={item.badge}
              nested
              onClick={onItemClick}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SidebarNav({
  messageItems,
  waitingDeviceCount,
  contactAdminUnread = 0,
  adminMessagesUnread = 0,
  onItemClick,
}: {
  messageItems: NavItem[];
  waitingDeviceCount: number;
  /** Tickets with an admin reply the Shop has not read. */
  contactAdminUnread?: number;
  /** Admin emails the Shop has not opened. */
  adminMessagesUnread?: number;
  onItemClick?: () => void;
}) {
  const cashierItems = useMemo(
    () =>
      CASHIER_NAV_ITEMS.map((item) =>
        item.to === CASHIER_DEVICES_PATH ? { ...item, badge: waitingDeviceCount } : item
      ),
    [waitingDeviceCount]
  );
  return (
    <>
      <SidebarNavItem to="/" end icon={Home} label={PAGE.dashboard} onClick={onItemClick} />
      <SidebarNavGroup label={GROUP.approvals} icon={ShieldCheck} items={APPROVALS_NAV_ITEMS} onItemClick={onItemClick} />
      <SidebarNavGroup label={GROUP.network} icon={Users} items={NETWORK_NAV_ITEMS} onItemClick={onItemClick} />
      <SidebarNavGroup
        label={GROUP.cashiers}
        icon={UserCircle}
        items={cashierItems}
        badge={waitingDeviceCount}
        onItemClick={onItemClick}
      />
      <SidebarNavGroup label={GROUP.transactions} icon={ArrowRightLeft} items={TRANSACTIONS_NAV_ITEMS} onItemClick={onItemClick} />
      <SidebarNavGroup label={GROUP.messages} icon={MessageSquare} items={messageItems} onItemClick={onItemClick} />
      <SidebarNavGroup label={GROUP.settings} icon={Settings} items={SETTINGS_NAV_ITEMS} onItemClick={onItemClick} />
      <SidebarNavItem to={ADMIN_MESSAGES_PATH} icon={Mail} label={ADMIN_MESSAGES_LABEL} badge={adminMessagesUnread} onClick={onItemClick} />
      <SidebarNavItem to={CONTACT_ADMIN_PATH} icon={LifeBuoy} label={CONTACT_ADMIN_LABEL} badge={contactAdminUnread} onClick={onItemClick} />
    </>
  );
}

function ShopAccountDropdown({
  user,
  onSignOut,
}: {
  user: { name?: string | null; email?: string | null } | null;
  onSignOut: () => void;
}) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white py-1 shadow-lg">
      <div className="border-b border-stone-100 px-3 py-2.5">
        <p className="text-[10px] font-medium uppercase tracking-wide text-stone-500">Shop</p>
        <p className="truncate text-sm font-semibold text-stone-900">{user?.name || 'Shop'}</p>
        {user?.email ? <p className="mt-1 truncate text-xs text-stone-500">{user.email}</p> : null}
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        Sign out
      </button>
    </div>
  );
}

export default function AppLayout({
  children,
}: {
  children: ReactNode;
  /** @deprecated Full width is now the default for all shop pages. */
  contentFullWidth?: boolean;
}) {
  const telegramSupportUrl = process.env.REACT_APP_CLIENT_TELEGRAM_SUPPORT_URL?.trim() || undefined;
  const androidAppUrl = process.env.REACT_APP_CLIENT_ANDROID_APK_URL?.trim() || undefined;
  const iosGuideUrl = process.env.REACT_APP_CLIENT_IOS_VIDEO_URL?.trim() || undefined;
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  /** Which surface opened the account menu (only one dropdown visible at a time). */
  const [accountMenu, setAccountMenu] = useState<'closed' | 'mobile' | 'desktop'>('closed');
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [usdBalance, setUsdBalance] = useState<number | null>(null);
  const [lowBalanceWarning, setLowBalanceWarning] = useState<LowBalanceWarningSettings>({
    enabled: true,
    effectiveThresholdUsd: null,
  });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [popupNavAllowed, setPopupNavAllowed] = useState<boolean | null>(null);
  const waitingDeviceCount = useWaitingDeviceCount(Boolean(user?.id));
  const contactAdminUnread = useContactAdminUnread(Boolean(user?.id));
  const adminMessagesUnread = useAdminMessagesUnread(Boolean(user?.id));

  const messageNavItems = useMemo(() => {
    if (popupNavAllowed !== true) {
      return MESSAGES_NAV_ITEMS.filter((item) => !POPUP_NAV_PATHS.has(item.to));
    }
    return MESSAGES_NAV_ITEMS;
  }, [popupNavAllowed]);

  /** Sidebar balance only — refetch on login/user change or explicit refresh (not on every route). */
  const loadSidebarBalance = useCallback(async () => {
    if (!user?.id) {
      setCreditBalance(null);
      setUsdBalance(null);
      setLowBalanceWarning({ enabled: true, effectiveThresholdUsd: null });
      setPopupNavAllowed(null);
      setBalanceLoading(false);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data != null) {
        setCreditBalance(res.data.credit_balance);
        setUsdBalance(res.data.online_balance_net ?? null);
        setLowBalanceWarning(parseLowBalanceWarningSettings(res.data));
        setPopupNavAllowed(!!res.data.can_delegate_popups);
      } else {
        setCreditBalance(null);
        setUsdBalance(null);
        setLowBalanceWarning({ enabled: true, effectiveThresholdUsd: null });
        setPopupNavAllowed(false);
      }
    } catch {
      setCreditBalance(null);
      setUsdBalance(null);
      setLowBalanceWarning({ enabled: true, effectiveThresholdUsd: null });
      setPopupNavAllowed(false);
    } finally {
      setBalanceLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadSidebarBalance();
  }, [loadSidebarBalance]);

  useEffect(() => {
    if (typeof user?.can_delegate_popups === 'boolean') {
      setPopupNavAllowed(user.can_delegate_popups);
    }
  }, [user]);

  useEffect(() => {
    const onAuth = () => {
      void loadSidebarBalance();
    };
    window.addEventListener(SHOP_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(SHOP_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [loadSidebarBalance]);

  useEffect(() => {
    const onRefresh = () => {
      void loadSidebarBalance();
    };
    window.addEventListener(SHOP_CREDIT_BALANCE_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(SHOP_CREDIT_BALANCE_REFRESH_EVENT, onRefresh);
  }, [loadSidebarBalance]);

  /** After user was away (e.g. payment tab) for a few seconds, refresh sidebar balance. */
  useEffect(() => {
    let lastHiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt = Date.now();
        return;
      }
      if (!user) return;
      if (!lastHiddenAt) return;
      if (Date.now() - lastHiddenAt < 2000) return;
      lastHiddenAt = 0;
      void loadSidebarBalance();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [user, loadSidebarBalance]);

  return (
    <div className="flex min-h-screen bg-stone-50 text-stone-900">
      <ShopInAppPopupGate />

      <aside className="hidden w-[280px] shrink-0 border-r border-stone-200 bg-white lg:flex lg:flex-col">
        <div className="border-b border-stone-200 px-4 pb-4 pt-5">
          <div className="flex items-center gap-2.5">
            <img src={vaultPlayLogo} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover shadow-sm" />
            <div>
              <p className="text-[15px] font-semibold tracking-tight text-stone-900">Vault Play</p>
              <p className="text-[11px] font-medium text-stone-500">{PORTAL_NAME.shop}</p>
            </div>
          </div>

          <div className="mt-3">
            <div className="space-y-2">
              <SidebarBalanceCard
                balance={creditBalance}
                loading={balanceLoading}
                lowBalanceWarning={lowBalanceWarning}
              />
              <SidebarUsdBalanceCard balance={usdBalance} loading={balanceLoading} />
            </div>
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-4">
          <SidebarNav
            messageItems={messageNavItems}
            waitingDeviceCount={waitingDeviceCount}
            contactAdminUnread={contactAdminUnread}
            adminMessagesUnread={adminMessagesUnread}
          />
        </nav>
      </aside>

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/35 lg:hidden"
            aria-label="Close navigation menu"
            onClick={() => {
              setMobileMenuOpen(false);
              setAccountMenu('closed');
            }}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-[86%] max-w-xs flex-col border-r border-stone-200 bg-white p-4 lg:hidden">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={vaultPlayLogo} alt="" className="h-8 w-8 shrink-0 rounded-md object-cover" />
                <div>
                  <div className="text-sm font-semibold">Vault Play</div>
                  <div className="text-[11px] font-medium text-stone-500">{PORTAL_NAME.shop}</div>
                </div>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-stone-600 hover:bg-stone-200"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="space-y-2">
                <SidebarBalanceCard
                balance={creditBalance}
                loading={balanceLoading}
                lowBalanceWarning={lowBalanceWarning}
              />
                <SidebarUsdBalanceCard balance={usdBalance} loading={balanceLoading} />
              </div>
            </div>
            <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
              <SidebarNav
                messageItems={messageNavItems}
                waitingDeviceCount={waitingDeviceCount}
                contactAdminUnread={contactAdminUnread}
                adminMessagesUnread={adminMessagesUnread}
                onItemClick={() => setMobileMenuOpen(false)}
              />
            </nav>
          </aside>
        </>
      )}

      <main className="relative min-w-0 flex-1 pb-24 lg:pb-0">
        <ShopDebtFreezeBanner />
        {accountMenu !== 'closed' ? (
          <button
            type="button"
            className="fixed inset-0 z-[15] cursor-default bg-black/0"
            aria-label="Close account menu"
            onClick={() => setAccountMenu('closed')}
          />
        ) : null}
        <header className="sticky top-0 z-20 border-b border-stone-200 bg-white/95 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-700"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1 text-center">
              <p className="truncate text-sm font-semibold text-stone-900">{user?.name || PORTAL_NAME.shop}</p>
              <p className="text-xs text-stone-500">Shop</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <VerifyNowTrigger />
              <ShopNotificationBell />
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAccountMenu((m) => (m === 'mobile' ? 'closed' : 'mobile'))}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                  aria-expanded={accountMenu === 'mobile'}
                  aria-haspopup="true"
                  aria-label="Account and sign out"
                >
                  <UserCircle className="h-6 w-6" strokeWidth={1.5} />
                </button>
                {accountMenu === 'mobile' ? (
                  <div className="absolute right-0 top-full z-[35] mt-2 w-[min(18rem,calc(100vw-2rem))]">
                    <ShopAccountDropdown
                      user={user}
                      onSignOut={() => {
                        setAccountMenu('closed');
                        logout();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>
        <header className="sticky top-0 z-20 hidden border-b border-stone-200 bg-white/95 backdrop-blur lg:block">
          <div className="flex items-center justify-between gap-4 px-10 py-4">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-stone-900">{user?.name || PORTAL_NAME.shop}</p>
              <p className="text-xs text-stone-500">Shop</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <VerifyNowTrigger />
              <ShopNotificationBell />
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAccountMenu((m) => (m === 'desktop' ? 'closed' : 'desktop'))}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-700 shadow-sm hover:bg-stone-50"
                  aria-expanded={accountMenu === 'desktop'}
                  aria-haspopup="true"
                  aria-label="Account and sign out"
                >
                  <UserCircle className="h-6 w-6" strokeWidth={1.5} />
                </button>
                {accountMenu === 'desktop' ? (
                  <div className="absolute right-0 top-full z-[35] mt-2 w-56">
                    <ShopAccountDropdown
                      user={user}
                      onSignOut={() => {
                        setAccountMenu('closed');
                        logout();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>
        <div className="w-full max-w-none px-4 pb-6 pt-5 sm:px-6 lg:px-10 lg:pt-6">
          {children}
        </div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-stone-200 bg-white/95 px-1 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 shadow-[0_-8px_20px_rgba(0,0,0,0.06)] backdrop-blur lg:hidden">
        <div className="flex w-full items-stretch gap-0.5">
          {MOBILE_NAV.map((item) => {
            const Icon = item.icon;
            const active = isShopNavPathActive(location.pathname, item);
            return (
              <NavLink
                key={item.to}
                to={item.to === '/popups/send' && popupNavAllowed !== true ? '/messages/compose' : item.to}
                end={item.end}
                className={`flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg px-0.5 py-1 text-[11px] font-medium ${
                  active ? 'bg-stone-200/90 text-stone-900' : 'text-stone-600'
                }`}
              >
                <span className="relative">
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.to === '/cashiers' ? (
                    <NavCountBadge count={waitingDeviceCount} className="absolute -right-3 -top-2" />
                  ) : null}
                </span>
                {/* Never cut the label: six slots are ~60px wide on a 375px phone, so a long word wraps
                    (hyphenated where the browser can) onto a second line instead of ending in "…". */}
                <span className="max-w-full whitespace-normal break-words hyphens-auto text-center text-[10px] leading-tight">
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      <SupportDownloadRail telegramUrl={telegramSupportUrl} androidUrl={androidAppUrl} iosUrl={iosGuideUrl} />
    </div>
  );
}
